import React, { useState, useRef, useEffect } from 'react';
import Badge from 'react-bootstrap/Badge';
import ReactJsonView from 'react-json-view';
import '../MqttEvent/MqttEvent.css';

const REMOTE_PALETTE = [
    '#58a6ff', '#3fb950', '#d29922', '#a371f7', '#f78166',
    '#56d364', '#79c0ff', '#ff7b72', '#ffa657', '#39d353',
];

function getRemoteColor(i) {
    return REMOTE_PALETTE[i % REMOTE_PALETTE.length];
}

// Convert hex string to flat bit array [0|1, ...]
function hexToBits(hex) {
    const bits = new Uint8Array(hex.length * 4);
    for (let i = 0; i < hex.length; i++) {
        const n = parseInt(hex[i], 16);
        if (isNaN(n)) continue;
        bits[i * 4]     = (n >> 3) & 1;
        bits[i * 4 + 1] = (n >> 2) & 1;
        bits[i * 4 + 2] = (n >> 1) & 1;
        bits[i * 4 + 3] =  n       & 1;
    }
    return bits;
}

function SignalCanvas({ raw }) {
    const canvasRef    = useRef(null);
    const [zoom, setZoom]       = useState(1);
    const [panFrac, setPanFrac] = useState(0);
    const isDragging   = useRef(false);
    const dragStartX   = useRef(0);
    const dragStartPan = useRef(0);

    const clampPan = (p, z) => Math.max(0, Math.min(1 - 1 / z, p));

    // Attach wheel listener as non-passive so preventDefault works
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const onWheel = (e) => {
            e.preventDefault();
            const rect = canvas.getBoundingClientRect();
            const cursorFrac = (e.clientX - rect.left) / rect.width;
            const factor  = e.deltaY < 0 ? 1.5 : 1 / 1.5;
            setZoom(prevZoom => {
                const newZoom = Math.max(1, Math.min(128, prevZoom * factor));
                const bitFrac = dragStartPan.current + cursorFrac / prevZoom;
                setPanFrac(prev => clampPan(bitFrac - cursorFrac / newZoom, newZoom));
                return newZoom;
            });
        };
        canvas.addEventListener('wheel', onWheel, { passive: false });
        return () => canvas.removeEventListener('wheel', onWheel);
    }, []);

    // Keep dragStartPan ref in sync so the wheel handler above can read it
    useEffect(() => { dragStartPan.current = panFrac; }, [panFrac]);

    // Draw
    useEffect(() => {
        if (!raw || !canvasRef.current) return;
        const allBits = hexToBits(raw);

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const W = canvas.width;
        const H = canvas.height;
        const topY = Math.round(H * 0.10);
        const botY = Math.round(H * 0.90);

        const winStart  = Math.floor(panFrac * allBits.length);
        const winEnd    = Math.min(allBits.length, Math.ceil((panFrac + 1 / zoom) * allBits.length));
        const bits      = allBits.subarray(winStart, winEnd);
        const bitsPerCol = bits.length / W;

        ctx.fillStyle = '#0d1117';
        ctx.fillRect(0, 0, W, H);

        ctx.strokeStyle = '#1c2128';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(0, H / 2);
        ctx.lineTo(W, H / 2);
        ctx.stroke();

        const levels = new Uint8Array(W);
        for (let x = 0; x < W; x++) {
            const s = Math.floor(x * bitsPerCol);
            const e = Math.ceil((x + 1) * bitsPerCol);
            const count = Math.max(1, e - s);
            let ones = 0;
            for (let b = s; b < e && b < bits.length; b++) ones += bits[b];
            levels[x] = (ones / count) >= 0.5 ? 1 : 0;
        }

        ctx.fillStyle = 'rgba(88,166,255,0.10)';
        let fillStart = null;
        for (let x = 0; x <= W; x++) {
            const hi = x < W ? levels[x] === 1 : false;
            if (hi && fillStart === null) { fillStart = x; }
            else if (!hi && fillStart !== null) {
                ctx.fillRect(fillStart, topY, x - fillStart, botY - topY);
                fillStart = null;
            }
        }

        ctx.strokeStyle = '#58a6ff';
        ctx.lineWidth = 1.5;
        ctx.lineJoin = 'miter';
        ctx.beginPath();
        let prevLvl = levels[0];
        ctx.moveTo(0, prevLvl ? topY : botY);
        for (let x = 1; x < W; x++) {
            const lvl = levels[x];
            if (lvl !== prevLvl) {
                ctx.lineTo(x, prevLvl ? topY : botY);
                ctx.lineTo(x, lvl    ? topY : botY);
                prevLvl = lvl;
            } else {
                ctx.lineTo(x, lvl ? topY : botY);
            }
        }
        ctx.stroke();

        // Minimap scrollbar when zoomed
        if (zoom > 1) {
            const sbH = 3, sbY = H - sbH - 1;
            ctx.fillStyle = 'rgba(255,255,255,0.08)';
            ctx.fillRect(0, sbY, W, sbH);
            ctx.fillStyle = '#58a6ff';
            ctx.fillRect(
                Math.round(panFrac * W),
                sbY,
                Math.max(4, Math.round(W / zoom)),
                sbH
            );
        }
    }, [raw, zoom, panFrac]);

    const handleMouseDown = (e) => {
        isDragging.current  = true;
        dragStartX.current  = e.clientX;
        dragStartPan.current = panFrac;
        e.currentTarget.style.cursor = 'grabbing';
    };

    const handleMouseMove = (e) => {
        if (!isDragging.current) return;
        const rect = canvasRef.current.getBoundingClientRect();
        const dx   = (e.clientX - dragStartX.current) / rect.width;
        setPanFrac(clampPan(dragStartPan.current - dx / zoom, zoom));
    };

    const stopDrag = (e) => {
        isDragging.current = false;
        if (e.currentTarget) e.currentTarget.style.cursor = zoom > 1 ? 'grab' : 'default';
    };

    const handleDblClick = () => { setZoom(1); setPanFrac(0); };

    return (
        <div>
            <canvas
                ref={canvasRef}
                width={900}
                height={80}
                style={{ width: '100%', height: '80px', borderRadius: '4px', display: 'block', cursor: zoom > 1 ? 'grab' : 'default' }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={stopDrag}
                onMouseLeave={stopDrag}
                onDoubleClick={handleDblClick}
            />
        </div>
    );
}

function RemoteDetectionEvent({ event }) {
    const payload = event.payload || {};
    const attrs   = payload.attributes || {};
    const { detected_remotes = [], raw, device_type, frequency, command } = attrs;

    const [showSignal, setShowSignal] = useState(false);
    const [showRaw, setShowRaw] = useState(false);

    const freqLabel = frequency
        ? (frequency >= 1_000_000
            ? (frequency / 1_000_000).toFixed(3) + ' MHz'
            : (frequency / 1000).toFixed(2) + ' kHz')
        : null;

    return (
        <div style={{ maxWidth: '100%', overflow: 'hidden' }}>

            {/* ── Row 1: Device Type · Frequency · Command  ›› remote badges ── */}
            <div className="d-flex align-items-center flex-wrap gap-1 mb-1">
                {device_type && (
                    <Badge bg="secondary" style={{ fontSize: '0.75rem' }}>{device_type}</Badge>
                )}
                {freqLabel && (
                    <Badge bg="secondary" style={{ fontSize: '0.75rem' }}>{freqLabel}</Badge>
                )}
                {command?.name && (
                    <Badge style={{ background: '#1f6feb', fontSize: '0.75rem' }}>{command.name}</Badge>
                )}

                {detected_remotes.length > 0 && (
                    <>
                        <span style={{ color: '#6e7681', fontSize: '1rem', fontWeight: 700, lineHeight: 1 }}>»</span>
                        {detected_remotes.map((remote, i) => (
                            <Badge
                                key={remote}
                                style={{
                                    background: getRemoteColor(i),
                                    color: '#0d1117',
                                    fontSize: '0.7rem',
                                    fontWeight: 700,
                                }}
                            >
                                {remote}
                            </Badge>
                        ))}
                    </>
                )}
            </div>

            {/* ── Row 2: {} button + Signal Data toggle ── */}
            <div className="d-flex align-items-center gap-1">
                <Badge
                    bg={showRaw ? 'secondary' : 'dark'}
                    style={{ cursor: 'pointer', fontSize: '0.75rem', userSelect: 'none', lineHeight: '1.5', padding: '0.35em 0.65em' }}
                    onClick={() => setShowRaw(s => !s)}
                    title="Toggle raw JSON"
                >
                    {'{}'}
                </Badge>
                {raw && (
                    <Badge
                        bg={showSignal ? 'secondary' : 'dark'}
                        style={{
                            cursor: 'pointer', fontSize: '0.75rem', userSelect: 'none',
                            lineHeight: '1.5', padding: '0.35em 0.65em',
                            minWidth: '100px', textAlign: 'center',
                            border: '1px solid rgba(255,255,255,0.2)',
                        }}
                        onClick={() => setShowSignal(s => !s)}
                        title={showSignal ? 'Hide signal data' : 'Show signal data'}
                    >
                        {showSignal ? '▲' : '▼'} Signal Data
                    </Badge>
                )}
            </div>

            {/* ── Signal section: waveform + hex textarea ── */}
            {showSignal && raw && (
                <div style={{ marginTop: '8px', maxWidth: '100%', overflow: 'hidden' }}>
                    <div style={{ background: '#0d1117', borderRadius: '6px', padding: '4px 6px 6px' }}>
                        <div style={{ color: '#6e7681', fontSize: '10px', marginBottom: '2px', fontFamily: 'monospace' }}>
                            RF signal — {(raw.length * 4).toLocaleString()} bits · {freqLabel}
                        </div>
                        <SignalCanvas raw={raw} />
                    </div>
                    <textarea
                        readOnly
                        value={raw}
                        rows={1}
                        spellCheck={false}
                        className="w-100 mt-1 rounded font-monospace"
                        style={{
                            background: 'rgb(13, 17, 23)',
                            color: 'rgb(88, 166, 255)',
                            border: '1px solid rgb(48, 54, 61)',
                            resize: 'none',
                            padding: '2px 6px',
                            whiteSpace: 'nowrap',
                            overflowX: 'hidden',
                        }}
                    />
                </div>
            )}

            {/* ── Raw JSON ── */}
            {showRaw && (
                <div style={{ marginTop: '6px', maxWidth: '100%', overflowX: 'auto' }}>
                    <ReactJsonView
                        src={payload}
                        className="mqttEventPayload"
                        theme="ocean"
                        borderStyle="none"
                        name={false}
                        displayObjectSize={false}
                        displayDataTypes={false}
                        collapsed={2}
                    />
                </div>
            )}
        </div>
    );
}

export default RemoteDetectionEvent;
